/**
 * @file feedback.jsx
 * @location src/components/ui/sharedui/feedback.jsx
 * @role 사용자 피드백 UI — EmptyState / Toast / ToastStack (모두 memo)
 */
import { memo } from 'react';
import { Icon } from '../Icon.jsx';

/**
 * 작은 회전 spinner — 폼 submit 중 / 비동기 작업 진행 표시.
 * 백엔드 합류 후 useAsyncAction 같은 헬퍼와 결합해 사용 예상.
 */
export const LoadingSpinner = memo(function LoadingSpinner({ size = 14, label = '불러오는 중' }) {
  return (
    <span className="loading-spinner" role="status" aria-label={label}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                strokeDasharray="42" strokeDashoffset="32" opacity="0.85" />
      </svg>
    </span>
  );
});

/**
 * 목록 비었을 때 안내 — icon name (string) 또는 이모지 자동 분기
 * tone: 'neutral' (default) | 'positive' (완료/순조) | 'info' (검색/필터 결과)
 */
export const EmptyState = memo(function EmptyState({ icon = 'inbox', tone = 'neutral', title, desc, action }) {
  const isEmoji = typeof icon === 'string' && /\p{Emoji}/u.test(icon);
  return (
    <div className={`empty-state empty-state-${tone}`}>
      <div className="empty-state-icon">
        {isEmoji ? icon : <Icon name={icon} size={32} />}
      </div>
      <div className="empty-state-title">{title}</div>
      {desc   && <div className="empty-state-desc">{desc}</div>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
});

/** 단일 Toast — 후방호환용. 스타일은 .toast / .toast.err 토큰으로 위임. */
export const Toast = memo(function Toast({ msg, type, show }) {
  return (
    <div className={`toast ${show ? 'show' : 'hide'}${type === 'err' ? ' err' : ''}`}>
      {msg}
    </div>
  );
});

/**
 * Toast 큐 스택 — App 레벨 (useToast hook 가 toasts 배열을 관리)
 * @param {object} props
 * @param {Array<{ id: string, msg: string, type?: string, action?: { label: string, onClick: () => void } }>} props.toasts
 * @param {(id: string) => void} [props.onDismiss]
 */
export const ToastStack = memo(function ToastStack({ toasts = [], onDismiss }) {
  return (
    <div className="toast-stack" role="region" aria-live="polite" aria-label="알림">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`toast show${t.type === 'err' ? ' err' : ''}${t.action ? ' with-action' : ''}`}
          role={t.type === 'err' ? 'alert' : 'status'}
        >
          <span>{t.msg}</span>
          {t.action && (
            <button
              type="button"
              className="toast-action-btn"
              aria-label={t.action.label}
              onClick={() => { t.action.onClick(); onDismiss?.(t.id); }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
});
